<template>
    <tab-section title="Contacts">
        <div class="uk-grid uk-grid-small" data-uk-grid-margin>
            <div class="uk-width-1-1">
                <div
                    id="project-main-contact-title"
                    :class="{'uk-text-required': !main_contact_project}"
                    class="uk-form-controls-text uk-text-bold uk-margin-bottom"
                >
                    <open-icon v-if="!main_contact_project" glyph="exclamation-triangle" />
                    Main Project Contact
                    <base-button
                        v-if="!!main_firm"
                        flavor="link"
                        title="Client Notes"
                        class="uk-margin-small-left uk-button-inline-patch"
                        @click.prevent="displaying_comments = !displaying_comments"
                    >
                        (
                            <open-icon v-if="!!comments.length" glyph="comments" />
                            <open-icon v-else glyph="comments-o" />
                            Client Notes
                        )
                    </base-button>
                </div>
                <div class="uk-flex">
                    <div class="uk-flex-item-1">
                        <form-select-combo
                            id="main-role"
                            name="role"
                            v-model="main_contact_project.role"
                            :error="has_error('role')"
                            :editable="editable"
                            :required="false"
                            @input="save_for_record_id(main_contact_project.id, $event, 'role')"
                        />
                    </div>
                    <div class="uk-flex-item-1 uk-margin-left">
                        <firm-picker
                            id="main-firm_id"
                            :value="main_firm?main_firm.id:0"
                            :editable="editable"
                            firm-type="-Utility"
                            @pick="save_for_record_id(main_contact_project.id, $event.id, 'firm_id')"
                        />
                    </div>
                    <div class="uk-flex-item-1 uk-margin-left">
                        <contact-picker
                            id="main-contact_id"
                            :value="main_contact?main_contact.id:0"
                            :email="main_contact?main_contact.email:''"
                            :editable="editable"
                            :firm-id="main_firm?main_firm.id:0"
                            :disabled="!main_firm||!main_firm.id"
                            @pick="save_for_record_id(main_contact_project.id, $event.id, 'contact_id')"
                        />
                    </div>
                </div>
            </div>

            <div v-if="!!main_contact" class="uk-width-2-3 uk-push-1-3">
                <form-text-area
                    v-if="editable"
                    id="notes"
                    entity="contact"
                    name="notes"
                    v-model="main_contact.notes"
                    placeholder="Main Project Contact Notes..."
                    width="1-1"
                    :required="false"
                    @input="save_field_for_record_id('contact', main_contact.id, $event, 'notes')"
                    class="uk-margin-top"
                />
                <p v-else-if="!!main_contact.notes">{{ main_contact.notes }}</p>
            </div>

            <div :class="{'lam-lower-contrast': !!wizardShown}" class="uk-width-1-1">
                <div class="uk-form-controls-text uk-text-bold">
                    Owner
                </div>
                <firm-picker
                    id="owner_id"
                    :value="record.owner_id"
                    :editable="editable"
                    :can-add="false"
                    @pick="save_field_for_record_id('project', record.id, $event.id, 'owner_id')"
                />
            </div>

            <div :class="{'lam-lower-contrast': !!wizardShown}" class="uk-width-1-1 uk-margin-top">
                <div class="uk-form-controls-text uk-text-bold">
                    Other Project Contacts
                    <a v-if="editable" href class="uk-button uk-button-mini uk-button-link" @click.prevent.stop="contact_project_add(null)">
                        <open-icon glyph="plus" />
                    </a>
                </div>
                <ul class="uk-list uk-list-space">
                    <li
                        v-for="(contact_project, index) in other_contact_projects"
                        :key="contact_project.id"
                        class="uk-grid uk-grid-small"
                        data-uk-grid-margin
                    >
                        <div class="uk-width-1-1">
                            <div class="uk-flex">
                                <div class="uk-flex-item-1">
                                    <form-select-combo
                                        :ref="`${contact_project.id}-role`"
                                        :id="`${contact_project.id}-role`"
                                        name="role"
                                        v-model="other_contact_projects[index].role"
                                        :error="has_error('role')"
                                        :editable="editable"
                                        :required="false"
                                        @input="save_for_record_id(contact_project.id, $event, 'role')"
                                    />
                                </div>
                                <div class="uk-flex-item-1 uk-margin-left">
                                    <firm-picker
                                        :id="`${contact_project.id}-firm_id`"
                                        :value="firm_for_contact_project(contact_project)?firm_for_contact_project(contact_project).id:0"
                                        :editable="editable"
                                        firm-type="-Utility"
                                        @pick="save_for_record_id(contact_project.id, $event.id, 'firm_id')"
                                    />
                                </div>
                                <div class="uk-flex-item-1 uk-margin-left">
                                    <contact-picker
                                        :id="`${contact_project.id}-contact_id`"
                                        :value="contact_for_contact_project(contact_project)?contact_for_contact_project(contact_project).id:0"
                                        :email="contact_for_contact_project(contact_project)?contact_for_contact_project(contact_project).email:''"
                                        :editable="editable"
                                        :firm-id="firm_for_contact_project(contact_project)?firm_for_contact_project(contact_project).id:0"
                                        :disabled="!firm_for_contact_project(contact_project)||!firm_for_contact_project(contact_project).id"
                                        @pick="save_for_record_id(contact_project.id, $event.id, 'contact_id')"
                                    />
                                </div>
                                <div v-if="editable" class="uk-flex-item-none uk-margin-small-left uk-text-right">
                                    <a href="#" v-if="editable" class="uk-button uk-button-danger uk-flipped-button" @click.prevent="remove_contact_project(contact_project)"><open-icon glyph="trash" /></a>
                                </div>
                            </div>
                        </div>

                        <div v-if="!!contact_for_contact_project(contact_project)" class="uk-width-2-3 uk-push-1-3">
                            <form-text-area
                                v-if="editable"
                                :id="`${contact_project.id}-notes`"
                                entity="contact"
                                name="notes"
                                v-model="contact_for_contact_project(contact_project).notes"
                                :placeholder="contact_notes_placeholder(contact_project)"
                                width="1-1"
                                :required="false"
                                @input="save_field_for_record_id('contact', contact_for_contact_project(contact_project).id, $event, 'notes')"
                                class="uk-margin-top"
                            />
                            <p v-else-if="!!contact_for_contact_project(contact_project).notes">{{ contact_for_contact_project(contact_project).notes }}</p>
                        </div>
                    </li>
                </ul>
            </div>

            <div :class="{'lam-lower-contrast': !!wizardShown}" class="uk-width-1-1">
                <div class="uk-form-controls-text uk-text-bold">
                    Subcontractors
                    <a v-if="editable" href class="uk-button uk-button-mini uk-button-link" @click.prevent.stop="contact_project_add('subcontractors')">
                        <open-icon glyph="plus" />
                    </a>
                </div>
                <ul class="uk-list uk-list-space">
                    <li
                        v-for="(contact_project, index) in subcontractors"
                        :key="contact_project.id"
                        class="uk-grid uk-grid-small"
                        data-uk-grid-margin
                    >
                        <div class="uk-width-1-1">
                            <div class="uk-flex">
                                <div class="uk-flex-item-1">
                                    <form-select-combo
                                        :ref="`${contact_project.id}-role`"
                                        :id="`${contact_project.id}-role`"
                                        name="role"
                                        v-model="subcontractors[index].role"
                                        :error="has_error('role')"
                                        :editable="editable"
                                        :required="false"
                                        @input="save_for_record_id(contact_project.id, $event, 'role')"
                                    />
                                </div>
                                <div class="uk-flex-item-1 uk-margin-left">
                                    <firm-picker
                                        :id="`${contact_project.id}-firm_id`"
                                        :value="firm_for_contact_project(contact_project)?firm_for_contact_project(contact_project).id:0"
                                        :editable="editable"
                                        firm-type="-Utility"
                                        @pick="save_for_record_id(contact_project.id, $event.id, 'firm_id')"
                                    />
                                </div>
                                <div class="uk-flex-item-1 uk-margin-left">
                                    <contact-picker
                                        :id="`${contact_project.id}-contact_id`"
                                        :value="contact_for_contact_project(contact_project)?contact_for_contact_project(contact_project).id:0"
                                        :email="contact_for_contact_project(contact_project)?contact_for_contact_project(contact_project).email:''"
                                        :editable="editable"
                                        :firm-id="firm_for_contact_project(contact_project)?firm_for_contact_project(contact_project).id:0"
                                        :disabled="!firm_for_contact_project(contact_project)||!firm_for_contact_project(contact_project).id"
                                        @pick="save_for_record_id(contact_project.id, $event.id, 'contact_id')"
                                    />
                                </div>
                                <div v-if="editable" class="uk-flex-item-none uk-margin-small-left uk-text-right">
                                    <a href="#" v-if="editable" class="uk-button uk-button-danger uk-flipped-button" @click.prevent="remove_contact_project(contact_project)"><open-icon glyph="trash" /></a>
                                </div>
                            </div>
                        </div>

                        <div v-if="!!contact_for_contact_project(contact_project)" class="uk-width-2-3 uk-push-1-3">
                            <form-text-area
                                v-if="editable"
                                :id="`${contact_project.id}-notes`"
                                entity="contact"
                                name="notes"
                                v-model="contact_for_contact_project(contact_project).notes"
                                :placeholder="contact_notes_placeholder(contact_project)"
                                width="1-1"
                                :required="false"
                                @input="save_field_for_record_id('contact', contact_for_contact_project(contact_project).id, $event, 'notes')"
                                class="uk-margin-top"
                            />
                            <p v-else-if="!!contact_for_contact_project(contact_project).notes">{{ contact_for_contact_project(contact_project).notes }}</p>
                        </div>
                    </li>
                </ul>
            </div>
        </div>

        <comments-dialog
            v-if="!!main_firm"
            :show.sync="displaying_comments"
            :title="`Comments for ${main_firm.name}`"
            :comments="comments"
            :owner-id="main_firm.id || 0"
            owner-type="App.Firm"
            :editable="editable"
        />
    </tab-section>
</template>

<script type="text/javascript">
import { mapGetters } from "vuex"

import is_project_relation_tab_section from "../mixins/IsProjectRelationTabSection"

import FormSelectCombo from "../../../ui/FormSelectCombo"
import FormTextArea from "../../../ui/FormTextArea"
import FirmPicker from "../../../ui/FirmPicker"
import ContactPicker from "../../../ui/ContactPicker"
import BaseButton from "../../../ui/BaseButton"
import OpenIcon from "../../../ui/OpenIcon"
import CommentsDialog from "../../../dialogs/CommentsDialog"

export default {
    mixins: [is_project_relation_tab_section],
    components: {
        FormSelectCombo,
        FormTextArea,
        FirmPicker,
        ContactPicker,
        BaseButton,
        OpenIcon,
        CommentsDialog
    },
    methods: {
        augment_query (query) {
            query.fields.push("ns::contact;fieldset::default")
            query.fields.push("ns::firm;fieldset::selection")
            return query
        },
        save_for_record_id (record_id, value, field) {
            this.save_field_for_record_id(this.entity, record_id, value, field)
        },
        contact_for_contact_project (contact_project) {
            if(!contact_project || !contact_project.firm_id) return null
            return this.raw_contact_records.find(contact => (contact.id === contact_project.contact_id))
        },
        firm_for_contact_project (contact_project) {
            if(!contact_project || !contact_project.firm_id) return null
            return this.raw_firm_records.find(firm => (firm.id === contact_project.firm_id))
        },
        contact_notes_placeholder (contact_project) {
            const contact = this.contact_for_contact_project(contact_project)
            if(contact && contact.role) return `Notes for ${contact.name} (${contact.role})`
            return "Contact Notes..."
        },
        remove_contact_project (contact_project) {
            const contact = this.contact_for_contact_project(contact_project)
            const firm = this.firm_for_contact_project(contact_project)

            let identifier = contact ? contact.name : ""
            if(firm) {
                if(identifier) identifier += ` (${firm.name})`
                else identifier = firm.name
            }
            this.$confirm({
                type: "warning",
                title: "Unlink Contact",
                message: `Do you really want to unlink ${identifier}? This will not delete the contact form Marge - just unlink it form the current project.`,
                cancel: "Keep It",
                ok: "Unlink It",
                then: (confirmed) => {
                    if(confirmed) {
                        this.$store.dispatch("RECORD_DELETE", {
                            entity: "contact_project",
                            id: contact_project.id
                        }).catch(error => {
                            if(error) this.$error(error.message)
                        })
                    }
                }
            })
        },
        contact_project_add (internal_category) {
            this.$store.dispatch("CREATE_RECORD", {
                entity: "contact_project",
                data: { project_id: this.record.id, internal_category }
            }).then(record => {
                const ref = this.$refs[`${record.id}-role`]
                if(ref && !!ref.length) {
                    const nodes = ref[0].$el.getElementsByTagName("select")
                    if(nodes && !!nodes.length) {
                        this.$nextTick(() => {
                            nodes[0].focus()
                        })
                    }
                }
            }).catch(error => {
                if(error) this.$error(error.message)
            })
        }
    },
    computed: {
        ...mapGetters(["entity_record"]),
        raw_contact_records () {
            return this.entity_records("contact")
        },
        raw_firm_records () {
            return this.entity_records("firm")
        },
        other_contact_projects () {
            if(!this.record || !this.record.id || !this.raw_records.length) return []
            return this.raw_records.filter(contact_project => !contact_project.internal_category && (contact_project.project_id === this.record.id))
        },
        main_contact_project () {
            if(!this.record || !this.record.id || !this.raw_records.length) return null
            return this.raw_records.find(contact_project => (contact_project.internal_category === "main") && (contact_project.project_id === this.record.id))
        },
        subcontractors () {
            if(!this.record || !this.record.id || !this.raw_records.length) return []
            return this.raw_records.filter(contact_project => (contact_project.internal_category === "subcontractors") && (contact_project.project_id === this.record.id))
        },
        main_contact () {
            return this.contact_for_contact_project(this.main_contact_project)
        },
        main_firm () {
            return this.firm_for_contact_project(this.main_contact_project)
        },
        comments () {
            const main_contact_project = this.main_contact_project
            if(!main_contact_project) return []
            return this.entity_records("comment").filter(comment => {
                return (comment.topic_type === "App\\Firm") && (comment.topic_id === main_contact_project.firm_id)
            })
        }
    },
    data () {
        return {
            entity: "contact_project",
            relation_ids: ["contact", "firm", "comment"],
            displaying_comments: false
        }
    }
}
</script>
